import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertOctagon, Mail, RotateCcw } from 'lucide-react';
import { Button } from '@shining-technologies/ui';
import { reportError } from '@/lib/observability';

interface Props {
  children: ReactNode;
  /** Optional reset key — changing it clears the error (e.g. route change). */
  resetKey?: string;
}
interface State {
  error: Error | null;
  requestId: string | null;
}

/** The one sanctioned class component — full-screen recovery for unhandled errors. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, requestId: null };

  static getDerivedStateFromError(error: Error): State {
    const requestId = (error as { requestId?: string }).requestId ?? null;
    return { error, requestId };
  }

  componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null, requestId: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, { componentStack: info.componentStack });
  }

  render() {
    if (!this.state.error) return this.props.children;
    const { requestId } = this.state;
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
        <AlertOctagon className="h-10 w-10 text-destructive" />
        <div className="space-y-1">
          <h1 className="text-lg font-semibold">Something went wrong</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            We hit an unexpected problem. Please try again, and let us know if it keeps happening.
          </p>
          {requestId && (
            <p className="text-xs text-muted-foreground">
              Error reference: <span className="font-mono">{requestId}</span>
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => this.setState({ error: null, requestId: null })}>
            <RotateCcw aria-hidden />
            Try again
          </Button>
          <Button asChild variant="ghost">
            <a
              href={`mailto:support@shiningtechnologies.com.au?subject=${encodeURIComponent(
                `Admin panel problem${requestId ? ` (ref ${requestId})` : ''}`,
              )}`}
              rel="noopener noreferrer"
            >
              <Mail aria-hidden />
              Report issue
            </a>
          </Button>
        </div>
      </div>
    );
  }
}
